import type { ClientLinkInviteInput, ServiceRelationshipConfig } from "@closer/shared";
import { clientLinkInviteSchema } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

export interface ClientLinkInviteDialogProps {
  open: boolean;
  clientId: string;
  /** Kayıttaki iletişim adresi — pencere bununla açılıyor, kullanıcı değiştirebiliyor. */
  /** Gerçek kişide davet müvekkilin KENDİ adresine; kurumda ilk yetkilinin (CLOS-1122 R1.4). */
  clientType: "individual" | "organization";
  varsayilanEposta: string | null;
  onClose: () => void;
}

/**
 * MÜVEKKİLİ CLOSER'A DAVET (CLOS-1107 · CLOS-1116 · K1 · ADR-0067).
 *
 * ### ⚠ Kullanıcıdan UUID İSTENMİYOR
 *
 * Denetimde ölçülen kusur buydu: yerel müvekkil kaydını Closer'daki karşılığına bağlamanın
 * tek yolu `lawFirmTenantId`/`targetTenantId` gibi bir kimlik yazmaktı ve ekranda o alanı
 * dolduracak hiçbir yüzey yoktu (R1 §3.3 — "kullanıcı teknik organization UUID/tenant ID
 * girmemeli"). Pencere adres soruyor; kullanıcı zaten bildiği bir şeyi yazıyor.
 *
 * ### ⚠ ADRESİN CLOSER'DAKİ KARŞILIĞI GÖNDERENE SIZMIYOR
 *
 * Sunucu adresten organizasyon ÇÖZMÜYOR: davet her durumda aynı sonucu veriyor ve
 * "bulunamadı" gibi bir cevap yok (R3 §21.2). Hangi organizasyonun adına kabul edileceğine
 * ALICI kendi oturumunda karar veriyor.
 *
 * ### ⚠ DAVET ERİŞİM DEĞİL
 *
 * Kabul edilene kadar hiçbir şey doğmuyor; kabul de yalnız "bu yerel kayıt hangi
 * organizasyona denk geliyor" sorusunu cevaplıyor. Sözleşme ve kişisel veri erişimi her
 * kayıt için ayrıca veriliyor — pencere bunu açıkça yazıyor ki kullanıcı davet ederken ne
 * verdiğini bilsin.
 */
export function ClientLinkInviteDialog({
  open,
  clientId,
  clientType,
  varsayilanEposta,
  onClose,
}: ClientLinkInviteDialogProps) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const politika = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "service-policy"),
    queryFn: async () =>
      (await api<ServiceRelationshipConfig>("/api/clients/service-policy", { tenantId })).data,
  });

  const kapsamlar = politika.data?.scopeTypes ?? [];

  const form = useZodForm(clientLinkInviteSchema, {
    defaultValues: {
      targetEmail: varsayilanEposta ?? "",
      scope: "",
    } as ClientLinkInviteInput,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const gonder = useMutation({
    mutationFn: (girdi: ClientLinkInviteInput) =>
      api(`/api/clients/${clientId}/service-relationships`, {
        method: "POST",
        tenantId,
        headers: { "idempotency-key": idempotency.uret() },
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      /**
       * ⚠ ANAHTAR "client" (TEKİL) — canlı ortamda ölçüldü (5 Eylül 2026: davet gönderildi,
       * rozet ancak sayfa yenilenince "Davet bekliyor" oldu). Detay sorgusu
       * `tenantKey(tenantId, "client", id)` ile yaşıyor; çoğul anahtar hiçbir sorguyu
       * eşleştirmiyordu ve geçersiz kılma sessizce hiçbir şey yapmıyordu.
       *
       * ⚠ Liste de tazeleniyor: müvekkil listesi bağ durumunu göstermese de aynı kaydın
       * başka bir görünümü ve bayat kalmasının bir sebebi yok.
       */
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "client", clientId) });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "clients") });
      /**
       * ⚠ MESAJ "GÖNDERİLDİ" DİYOR, "BULUNDU" DEMİYOR: ekran, sunucunun bilinçle
       * sızdırmadığı bilgiyi sızdırmamalı.
       */
      toast.success("Davet gönderildi");
      kapat();
    },
    onError: (hata: unknown) => {
      applyServerErrors(form, hata);
      toast.error(hata instanceof ApiRequestError ? hata.message : "Davet gönderilemedi");
    },
  });

  function kapat() {
    form.reset({ targetEmail: varsayilanEposta ?? "", scope: "" } as ClientLinkInviteInput);
    onClose();
  }

  return (
    <Modal open={open} onClose={kapat} title="Closer'a davet et">
      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((values) => gonder.mutate(values))}
      >
        {/*
          TEMSİLİ → CLOSER DÖNÜŞÜMÜ (CLOS-1143, rapor §10): aynı kayıt, aynı sözleşmeler; yalnız
          Closer ilişki/davet/alan tarafı eklenir. Davet kabul edilene kadar ilişki aktif olmaz.
        */}
        <p className="text-sm text-fg-soft">
          {clientType === "individual"
            ? "Müvekkili Closer'a davet edin; davet müvekkilin kendi e-postasına gider. Kabul edilene kadar ilişki ve Müvekkil Alanı aktif olmaz."
            : "Kurumu Closer'a davet edin; davet kurum adına kabul edecek ilk yetkilinin e-postasına gider. Kabul edilene kadar ilişki ve Müvekkil Alanı aktif olmaz."}
        </p>

        <FormField
          label={clientType === "individual" ? "Müvekkilin e-postası" : "İlk yetkili e-postası"}
          error={form.formState.errors.targetEmail?.message}
        >
          <Input id="davet-eposta" type="email" required {...form.register("targetEmail")} />
        </FormField>

        <FormField label="Hizmet kapsamı" error={form.formState.errors.scope?.message}>
          <Select id="davet-kapsam" required {...form.register("scope")}>
            <option value="">Hizmet kapsamı seçin</option>
            {kapsamlar.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={kapat}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={gonder.isPending}>
            Davet gönder
          </Button>
        </div>
      </form>
    </Modal>
  );
}
